import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import BaseTile from '../../../components/Tile/BaseTile';
import TileContent, { TileStatusContent } from '../../../components/Tile/TileContent';
import RefreshButton from '../../../components/Tile/RefreshButton';
import { useLocalFilters } from "../../../hooks/useLocalFilters";
import { useAutoLoad } from "../../../hooks/useAutoLoad";
import { getDynamicAttributeStats } from "./api/attributeFilteredStatsApi";
import { getOCELMetadata, makeRequest } from "../../../services/apiClient";
import TileSettingsModal from '../../../components/common/TileSettingsModal';
import LeadTimeStatisticsSettings from './LeadTimeStatisticsSettings';
import LocalFiltersActiveBadge from '../../../components/common/LocalFiltersActiveBadge';

// Number size scales down as more stats share one row, so 1-2 stats read as
// big as the single-number KPI tiles (text-5xl) while 4-5 still fit.
const NUMBER_SIZE_BY_COUNT = { 1: 'text-5xl', 2: 'text-5xl', 3: 'text-4xl' };
const getNumberSizeClass = (count) => NUMBER_SIZE_BY_COUNT[count] || 'text-3xl';

// Number row height matches each size class's own (leading-none) line height
// exactly — h-12=48px for text-5xl, h-9=36px for text-4xl, h-7.5=30px for
// text-3xl — so the row never reserves more vertical space than the glyph
// itself needs. A mismatch here (e.g. always h-12) leaves dead space above
// and below smaller numbers, inflating the gap to the title/label above/below.
const NUMBER_ROW_HEIGHT_BY_COUNT = { 1: 'h-12', 2: 'h-12', 3: 'h-9' };
const getNumberRowHeightClass = (count) => NUMBER_ROW_HEIGHT_BY_COUNT[count] || 'h-7.5';

// StatCard Component — number sits directly under the title (gap-1), with a
// row height matched to its own font size (see getNumberRowHeightClass) so no
// extra vertical space is reserved. The label sits tight under the number
// (gap-0.5) at the same style as that tile's period-label row (text-xs
// text-gray-400).
const StatCard = ({ label, value, hasError, sizeClass, rowHeightClass }) => (
  <div className="flex flex-col items-center gap-0.5">
    <div className={`${rowHeightClass} flex items-center`}>
      <p className={hasError ? "text-xl font-bold text-red-600" : `${sizeClass} font-bold tracking-tight text-primary-600 leading-none`}>
        {hasError ? "Error" : (value !== null && value !== undefined ?
          (label === "Count" ? Math.round(value).toLocaleString() : value.toFixed(1)) : "—")}
      </p>
    </div>
    <p className="text-xs text-gray-400 leading-tight">{label}</p>
  </div>
);

const formatLeadTime = (hours) => {
  if (hours == null) return { value: hours, unit: 'h' };
  return hours >= 48 ? { value: hours / 24, unit: 'days' } : { value: hours, unit: 'hours' };
};

const formatPeriod = (start, end) => {
  if (!start || !end) return null;
  return `${new Date(start).toLocaleDateString()} – ${new Date(end).toLocaleDateString()}`;
};

// Maps a visibleStats id to the StatCard it renders
const STAT_CARD_BUILDERS = {
  min: (data, hasError, sizeClass, rowHeightClass) => {
    const fmt = formatLeadTime(data?.min_lead_time);
    return <StatCard key="min" label={`Minimum (${fmt.unit})`} value={fmt.value} hasError={hasError} sizeClass={sizeClass} rowHeightClass={rowHeightClass} />;
  },
  median: (data, hasError, sizeClass, rowHeightClass) => {
    const fmt = formatLeadTime(data?.median_lead_time);
    return <StatCard key="median" label={`Median (${fmt.unit})`} value={fmt.value} hasError={hasError} sizeClass={sizeClass} rowHeightClass={rowHeightClass} />;
  },
  max: (data, hasError, sizeClass, rowHeightClass) => {
    const fmt = formatLeadTime(data?.max_lead_time);
    return <StatCard key="max" label={`Maximum (${fmt.unit})`} value={fmt.value} hasError={hasError} sizeClass={sizeClass} rowHeightClass={rowHeightClass} />;
  },
  average: (data, hasError, sizeClass, rowHeightClass) => {
    const fmt = formatLeadTime(data?.mean_lead_time);
    return <StatCard key="average" label={`Average (${fmt.unit})`} value={fmt.value} hasError={hasError} sizeClass={sizeClass} rowHeightClass={rowHeightClass} />;
  },
  count: (data, hasError, sizeClass, rowHeightClass) => (
    <StatCard key="count" label="Count" value={data?.count_objects} hasError={hasError} sizeClass={sizeClass} rowHeightClass={rowHeightClass} />
  ),
};

// AttributeRow Component (reusable and simple)
const AttributeRow = ({ attributeName, attributeValue, auftragData, hasError, visibleStats, showPeriod }) => {
  const sizeClass = getNumberSizeClass(visibleStats.length);
  const rowHeightClass = getNumberRowHeightClass(visibleStats.length);
  const statCards = visibleStats
    .map((id) => STAT_CARD_BUILDERS[id]?.(auftragData, hasError, sizeClass, rowHeightClass))
    .filter(Boolean);
  const period = showPeriod ? formatPeriod(auftragData?.period_start, auftragData?.period_end) : null;

  if (attributeName === "Summary") {
    return (
      <div className="flex flex-col gap-1">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(90px,1fr))] justify-items-center gap-x-2 gap-y-1 w-full">{statCards}</div>
        {period && <p className="text-xs text-gray-400 text-center">{period}</p>}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="text-sm font-medium text-gray-700 text-center">
        {attributeName}
        {attributeValue && attributeValue !== "all" && (
          <span className="text-gray-400"> ({attributeValue})</span>
        )}
      </div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(90px,1fr))] justify-items-center gap-x-2 gap-y-1 w-full">{statCards}</div>
      {period && <p className="text-xs text-gray-400 text-center">{period}</p>}
    </div>
  );
};

const LeadTimeStatistics = ({ activeFile, onRefresh, onGetHeaderActions, defaultObjectType, onRegisterSettingsGetter, initialSettings, onTitleChange }) => {
  const { t } = useTranslation();
  // ✅ Use local filters hook - automatically uses local or global filters
  const {
    localFilters,
    updateLocalFilter,
    clearLocalFilters,
    hasLocalFilters,
    localFilterCount,
    api,
    currentFilters,
    startDraftMode,
    applyDraftFilters,
    cancelDraftFilters
  } = useLocalFilters(activeFile, initialSettings?.localFilters ?? null);

  const [statsData, setStatsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [objectTypes, setObjectTypes] = useState([]);
  const [selectedObjectType, setSelectedObjectType] = useState(null); // Start with null, will be set from metadata
  const [draftObjectType, setDraftObjectType] = useState(null);
  const [componentTitle, setComponentTitle] = useState('Lead Time Statistics');
  const [draftTitle, setDraftTitle] = useState('Lead Time Statistics');

  const [startEvents, setStartEvents] = useState([]);
  const [endEvents, setEndEvents] = useState([]);
  const [draftStartEvents, setDraftStartEvents] = useState([]);
  const [draftEndEvents, setDraftEndEvents] = useState([]);
  const [activitiesForObjectType, setActivitiesForObjectType] = useState([]);

  const DEFAULT_VISIBLE_STATS = ['min', 'median', 'max', 'average', 'count'];
  const [visibleStats, setVisibleStats] = useState(DEFAULT_VISIBLE_STATS);
  const [draftVisibleStats, setDraftVisibleStats] = useState(DEFAULT_VISIBLE_STATS);
  const [showPeriod, setShowPeriod] = useState(false);
  const [draftShowPeriod, setDraftShowPeriod] = useState(false);

  // Ref so useCallback fetchData always reads the latest value without needing it as a dep
  const selectedObjectTypeRef = useRef(selectedObjectType);
  selectedObjectTypeRef.current = selectedObjectType;

  // Settings modal state
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // Restore settings from saved config (mount-only)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!initialSettings) return;
    if (initialSettings.selectedObjectType !== undefined) setSelectedObjectType(initialSettings.selectedObjectType);
    if (initialSettings.componentTitle !== undefined) setComponentTitle(initialSettings.componentTitle);
    if (initialSettings.startEvents !== undefined) setStartEvents(initialSettings.startEvents);
    if (initialSettings.endEvents !== undefined) setEndEvents(initialSettings.endEvents);
    if (initialSettings.visibleStats !== undefined) setVisibleStats(initialSettings.visibleStats);
    if (initialSettings.showPeriod !== undefined) setShowPeriod(initialSettings.showPeriod);
  }, []);

  // Register settings getter with parent so it can be collected on Save
  useEffect(() => {
    onRegisterSettingsGetter?.(() => ({ selectedObjectType, componentTitle, localFilters, startEvents, endEvents, visibleStats, showPeriod }));
  }, [selectedObjectType, componentTitle, localFilters, startEvents, endEvents, visibleStats, showPeriod]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-sync the tile title to the parent header — also fires after the
  // mount-restore effect applies a saved componentTitle.
  const onTitleChangeRef = useRef(onTitleChange);
  useEffect(() => { onTitleChangeRef.current = onTitleChange; });
  useEffect(() => {
    onTitleChangeRef.current?.(componentTitle || "Lead Time Statistics");
  }, [componentTitle]);

  const handleOpenSettings = React.useCallback(() => {
    startDraftMode();
    setDraftObjectType(selectedObjectType);
    setDraftTitle(componentTitle);
    setDraftStartEvents(startEvents);
    setDraftEndEvents(endEvents);
    setDraftVisibleStats(visibleStats);
    setDraftShowPeriod(showPeriod);
    setIsSettingsModalOpen(true);
  }, [startDraftMode, selectedObjectType, componentTitle, startEvents, endEvents, visibleStats, showPeriod]);

  const handleSettingsOpenChange = React.useCallback((val) => {
    setIsSettingsModalOpen(val);
    if (!val) cancelDraftFilters();
  }, [cancelDraftFilters]);

  const handleApplySettings = React.useCallback(() => {
    applyDraftFilters();
    setSelectedObjectType(draftObjectType);
    setComponentTitle(draftTitle);
    setStartEvents(draftStartEvents);
    setEndEvents(draftEndEvents);
    setVisibleStats(draftVisibleStats);
    setShowPeriod(draftShowPeriod);
    onTitleChange?.(draftTitle);
    setIsSettingsModalOpen(false);
  }, [applyDraftFilters, draftObjectType, draftTitle, draftStartEvents, draftEndEvents, draftVisibleStats, draftShowPeriod, onTitleChange]);

  // Fetch activities for the selected object type; filter stale event selections after load
  useEffect(() => {
    setActivitiesForObjectType([]);
    if (!selectedObjectType || !activeFile) return;
    let cancelled = false;
    makeRequest('/object-type-activities', { params: { file_type: activeFile } }, 'Object Type Activities')
      .then((res) => {
        if (cancelled) return;
        const acts = res?.objectTypeActivities?.[selectedObjectType] || [];
        setActivitiesForObjectType(acts);
        // Keep the same array identity when nothing is dropped, so fetchData
        // (which depends on these) isn't recreated and refetched needlessly.
        setStartEvents(prev => (prev.every(e => acts.includes(e)) ? prev : prev.filter(e => acts.includes(e))));
        setEndEvents(prev => (prev.every(e => acts.includes(e)) ? prev : prev.filter(e => acts.includes(e))));
      })
      .catch(() => { if (!cancelled) setActivitiesForObjectType([]); });
    return () => { cancelled = true; };
  }, [selectedObjectType, activeFile]);

  // While the settings modal is open, react live to draft object type changes
  // (instead of waiting for Apply to sync selectedObjectType). Filters the
  // draft event selections, not the committed ones.
  useEffect(() => {
    if (!isSettingsModalOpen) return;
    if (!draftObjectType || !activeFile) {
      setActivitiesForObjectType([]);
      return;
    }
    let cancelled = false;
    makeRequest('/object-type-activities', { params: { file_type: activeFile } }, 'Object Type Activities')
      .then((res) => {
        if (cancelled) return;
        const acts = res?.objectTypeActivities?.[draftObjectType] || [];
        setActivitiesForObjectType(acts);
        setDraftStartEvents(prev => prev.filter(e => acts.includes(e)));
        setDraftEndEvents(prev => prev.filter(e => acts.includes(e)));
      })
      .catch(() => { if (!cancelled) setActivitiesForObjectType([]); });
    return () => { cancelled = true; };
  }, [isSettingsModalOpen, draftObjectType, activeFile]);

  // Fetch available object types from metadata and filter based on global filter
  useEffect(() => {
    const fetchObjectTypes = async () => {
      if (!activeFile) return;
      try {
        const metadata = await getOCELMetadata(activeFile);
        console.log('📊 LeadTimeStatistics - Fetched metadata:', metadata);

        if (metadata?.object_types) {
          // Handle both array and object format (LeadTimeHistogram uses object format)
          let allTypes;
          if (Array.isArray(metadata.object_types)) {
            allTypes = metadata.object_types;
          } else if (typeof metadata.object_types === 'object') {
            allTypes = Object.keys(metadata.object_types);
          }

          if (allTypes && allTypes.length > 0) {
            // ✅ Filter object types based on global filter
            let filteredTypes = allTypes;
            if (api.currentFilters?.objectTypes && api.currentFilters.objectTypes.length > 0) {
              filteredTypes = allTypes.filter(type =>
                api.currentFilters.objectTypes.includes(type)
              );
              console.log(`🔍 Global filter applied: ${allTypes.length} types → ${filteredTypes.length} types`);
            }

            setObjectTypes(filteredTypes);

            // Read via ref, not the closed-over state var — this callback runs after an
            // async metadata fetch, and by then a mount-time restore of initialSettings
            // may have already updated selectedObjectType while this closure still saw null.
            const currentSelection = selectedObjectTypeRef.current;

            // ✅ Apply default object type if provided and valid
            if (defaultObjectType && filteredTypes.includes(defaultObjectType) && !currentSelection) {
              setSelectedObjectType(defaultObjectType);
              setHasFetched(true); // Mark as configured to enable auto-load
              console.log(`✅ Applied default object type: '${defaultObjectType}', will auto-load`);
            }
            // Set first object type as default if:
            // 1. No object type is currently selected, OR
            // 2. Current selection is not in the available/filtered list
            else if (!currentSelection || !filteredTypes.includes(currentSelection)) {
              if (filteredTypes.length > 0) {
                const newSelectedType = filteredTypes[0];
                setSelectedObjectType(newSelectedType);
                console.log(`✅ Object type set to '${newSelectedType}' (first available type from: ${filteredTypes.join(', ')})`);
              } else {
                setSelectedObjectType(null);
                console.warn("⚠️ No object types available after global filter");
              }
            } else {
              console.log(`✅ Keeping current selection: '${currentSelection}' (still valid in: ${filteredTypes.join(', ')})`);
            }
          }
        }
      } catch (err) {
        console.error('❌ Failed to fetch object types:', err);
        // Don't set fallback types - let it remain empty so user knows there's an issue
        setObjectTypes([]);
        setSelectedObjectType(null);
      }
    };

    if (activeFile) {
      fetchObjectTypes();
    }
  }, [activeFile, api.currentFilters?.objectTypes]);

  // Only the most recently started fetch may commit its result — a slower,
  // older request (e.g. superseded by a filter change or the post-restore
  // refetch) must not overwrite a newer one.
  const fetchSeqRef = useRef(0);
  const fetchData = useCallback(async () => {
    const objectType = selectedObjectTypeRef.current;
    // Guard: Don't fetch if no object type is selected
    if (!objectType) {
      console.warn("⚠️ Cannot fetch stats: No object type selected");
      setLoading(false);
      return;
    }

    const seq = ++fetchSeqRef.current;
    setLoading(true);
    setError(null);
    setHasFetched(true);

    try {
      const useCustomEvents = startEvents.length > 0 && endEvents.length > 0;
      const endpoint = useCustomEvents ? '/kpi/custom-dynamic-attribute-stats' : '/kpi/dynamic-attribute-stats';
      const requestBody = useCustomEvents
        ? { object_type: objectType, start_events: startEvents, end_events: endEvents }
        : { object_type: objectType };
      console.log("📤 Sending Attribute Stats request:", { endpoint, ...requestBody });
      const response = await api.post(endpoint, requestBody, {}, 'Attribute Stats');
      console.log("🚀 Received Attribute Stats Response:", response);
      if (seq !== fetchSeqRef.current) return;
      setStatsData(response);
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      console.error("Attribute Stats Fetch Error:", err);
      setError(err.message || "An unknown error occurred.");
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [api, startEvents, endEvents]);


  // Auto-fetch on first mount once objectType is resolved
  useEffect(() => {
    if (selectedObjectType && !hasFetched) {
      fetchData();
    }
  }, [selectedObjectType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ✅ Auto-reload when filters or object type change
  useEffect(() => {
    if (hasFetched) {
      fetchData();
    }
  }, [currentFilters, activeFile, selectedObjectType, fetchData]);

  const rowsToDisplay = useMemo(() => {
    if (!statsData || statsData.length === 0) return [];
    console.log("🔍 statsData in LeadTimeStatistics:", statsData);
    
    // Get non-Overall data first
    const nonOverallData = statsData.filter(d => d.attribute_name !== "Overall");
    
    // If we have non-Overall data, use that
    if (nonOverallData.length > 0) {
      return nonOverallData;
    }
    
    // If we only have Overall data, transform it to show without "Overall" label
    const overallData = statsData.filter(d => d.attribute_name === "Overall");
    if (overallData.length > 0) {
      // Transform Overall data to show as "Summary" or just hide the attribute name
      return overallData.map(item => ({
        ...item,
        attribute_name: "Summary",
        attribute_value: null // Hide the "(all)" text
      }));
    }
    
    return [];
  }, [statsData]);

  const hasData = !loading && !error && rowsToDisplay.length > 0;
  const showStats = hasFetched && !loading && !error && hasData;
  const showNoDataMessage = hasFetched && !loading && !error && !hasData;
  const displayTitle = componentTitle || "Lead Time Statistics";

  // Header actions - settings button and local filters active badge
  const headerActions = React.useMemo(() => (
    <div className="flex items-center gap-2">
      {selectedObjectType && (
        <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 text-primary-700 rounded-full border border-primary-200">
          {selectedObjectType}
        </span>
      )}
      <LocalFiltersActiveBadge
        hasLocalFilters={hasLocalFilters}
        localFilterCount={localFilterCount}
        onClearLocalFilters={clearLocalFilters}
      />
      <button
        onClick={handleOpenSettings}
        className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        aria-label={t('common:actions.openSettings')}
      >
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        <span className="text-xs">{t('common:actions.settings')}</span>
      </button>
    </div>
  ), [selectedObjectType, handleOpenSettings, hasLocalFilters, localFilterCount, clearLocalFilters]);

  // Auto-load data when persistent cache is enabled
  useAutoLoad(fetchData, [activeFile, api.currentFilters], hasData);

  // Provide refresh function to parent (GridComponent)
  React.useEffect(() => {
    if (onRefresh) {
      onRefresh(fetchData);
    }
  }, [onRefresh, fetchData]);

  // Provide header actions to parent (GridComponent)
  React.useEffect(() => {
    if (onGetHeaderActions) {
      onGetHeaderActions(headerActions);
    }
  }, [onGetHeaderActions, headerActions]);

  return (
    <BaseTile
      loading={loading}
      error={error}
      minHeight="auto"
      padding="sm"
      className=""
    >
      <TileContent layout="flex" direction="column" align="stretch" justify="center" grow>
        {/* Loading State */}
        {loading && (
           <TileStatusContent status="loading" />
        )}

        {/* Error State with Retry */}
        {!loading && error && (
           <TileStatusContent 
             status="error" 
             title="Failed to load stats" 
             message={error}
             action={
               <button
                 onClick={fetchData}
                 className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
               >
                 Retry
               </button>
             }
           />
        )}
        
        {/* Statistics Display — same wrapper (gap-1 py-1, items-center) and
            title styling (text-sm font-medium text-gray-700) as the
            single-value KPI tiles (ObjectCountKPI/ObjectConversionKPI), so
            the title row lands at the same height and size across tiles. */}
        {showStats && (
          <div className="w-full flex flex-col gap-1 py-1 items-center">
            <div className="text-sm font-medium text-gray-700">{displayTitle}</div>
            <div className="flex flex-col gap-3 w-full overflow-y-auto">
              {rowsToDisplay.map((row, index) => (
                <AttributeRow
                  key={`${row.attribute_name}-${row.attribute_value}-${index}`}
                  attributeName={row.attribute_name}
                  attributeValue={row.attribute_value}
                  auftragData={row.auftrag_data}
                  hasError={!!row.error}
                  visibleStats={visibleStats}
                  showPeriod={showPeriod}
                />
              ))}
            </div>
          </div>
        )}

        {/* No Data Message */}
        {showNoDataMessage && (
          <TileStatusContent
            status="empty"
            title="No Statistics Available"
            message="No data matches the current filter criteria."
            action={
              <button
                onClick={fetchData}
                className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Retry
              </button>
            }
          />
        )}
      </TileContent>

      {/* Settings Modal */}
      <TileSettingsModal
        open={isSettingsModalOpen}
        onOpenChange={handleSettingsOpenChange}
        onApply={handleApplySettings}
        title="Attribute Filtered Stats Settings"
      >
        <LeadTimeStatisticsSettings
          objectType={draftObjectType}
          onObjectTypeChange={setDraftObjectType}
          objectTypes={objectTypes}
          loading={loading}
          localFilters={localFilters}
          updateLocalFilter={updateLocalFilter}
          activeFile={activeFile}
          componentTitle={draftTitle}
          onTitleChange={setDraftTitle}
          startEvents={draftStartEvents}
          onStartEventsChange={setDraftStartEvents}
          endEvents={draftEndEvents}
          onEndEventsChange={setDraftEndEvents}
          activitiesForObjectType={activitiesForObjectType}
          visibleStats={draftVisibleStats}
          onVisibleStatsChange={setDraftVisibleStats}
          showPeriod={draftShowPeriod}
          onShowPeriodChange={setDraftShowPeriod}
        />
      </TileSettingsModal>
    </BaseTile>
  );
};

export default LeadTimeStatistics;